import { FC, memo } from "react";
import { DateTime } from "luxon";
import slugify from "slugify";
import styled from "styled-components";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";

import {
  InfoCardBox,
  InfoCardGroup,
  InfoCardGroupFlex,
  InfoCardGroupFlexInner,
  InfoCardGroupsWrapper,
  InfoCardItem,
} from "@/components/Testing";
import Text from "@/components/Text";
import ExportButton from "@/components/ExportButton";
import { OptimisationProjectResource } from "@/types/resources";
import {
  OptimisationProjectStatusEnum,
  TestingCurrencies,
} from "@/utils/enums";
import { OptimisationProjectNetworkEnum } from "@/utils/enums";
import { api } from "@/utils/api";

const ExportButtonHolder = styled.div`
  position: absolute;
  top: 24px;
  right: 24px;
`;

const StyledExportButton = styled(ExportButton)`
  padding-right: 6px;
  padding-left: 14px;
`;

interface Props {
  project: OptimisationProjectResource;
}

const OptimisationTestInfoCard: FC<Props> = memo(({ project }) => {
  const renderProjectStatus = () => {
    switch (project.status) {
      case OptimisationProjectStatusEnum.DRAFT:
        return "Draft";
      case OptimisationProjectStatusEnum.RUNNING:
        return "Running";
      case OptimisationProjectStatusEnum.ENDED:
        return "Ended";
      default:
        return "Unknown";
    }
  };

  const exportOptions = [
    {
      fileName: `Testing-${slugify(project.name)}.csv`,
      title: "Google Ads CSV",
      fetchCsv: () =>
        api.get<never, string>(`/v1/optimisation-tests/${project.id}/ga-csv`),
    },
    {
      fileName: `${slugify(project.name)}-results.csv`,
      title: "Results CSV",
      fetchCsv: () =>
        api.get<never, string>(`/v1/optimisation-tests/${project.id}/export`),
    },
    {
      fileName: `${slugify(project.name)}-results-changes.csv`,
      title: "Results Changes CSV",
      fetchCsv: () =>
        api.get<never, string>(
          `/v1/optimisation-tests/${project.id}/export-stats-log`
        ),
    },
    {
      fileName: `${slugify(project.name)}-eYTV-top-5.csv`,
      title: "eYTV Top 5 CSV",
      fetchCsv: () =>
        api.get<never, string>(`/v1/optimisation-tests/${project.id}/export`, {
          params: {
            "filter[top]": 5,
          },
        }),
    },
  ];

  const { data: currencyRateKrvToUsd } = useQuery(
    [`${TestingCurrencies.KRW}_to_usd_currency_rate`],
    () =>
      axios
        .get(
          `https://api.exchangerate.host/convert?from=${TestingCurrencies.KRW}&to=USD&amount=100`
        )
        .then((response) => response.data?.info?.rate),
    {
      staleTime: 300 * 1000,
    }
  );

  const { data: currencyRateKrvToEur } = useQuery(
    [`${TestingCurrencies.KRW}_to_eur_currency_rate`],
    () =>
      axios
        .get(
          `https://api.exchangerate.host/convert?from=${TestingCurrencies.KRW}&to=USD&amount=100`
        )
        .then((response) => response.data?.info?.rate),
    {
      staleTime: 300 * 1000,
    }
  );

  const renderProjectSpendings = () => {
    if (!project.spendings) {
      return "-";
    }

    if (project.network === OptimisationProjectNetworkEnum.FACEBOOK) {
      return `€ ${project.spendings ?? "-"}`;
    }

    return (
      <>
        ₩ {project.spendings}
        {!!currencyRateKrvToUsd && (
          <> / $ {(project.spendings * currencyRateKrvToUsd).toFixed(2)}</>
        )}
        {!!currencyRateKrvToEur && (
          <> / € {(project.spendings * currencyRateKrvToEur).toFixed(2)}</>
        )}
      </>
    );
  };

  return (
    <InfoCardBox>
      <InfoCardGroupsWrapper>
        <InfoCardGroup>
          {project.projectId && (
            <InfoCardItem>
              <Text $type="smallBold" $color="gray">
                Project ID
              </Text>
              <Text $type="caption">{project.projectId}</Text>
            </InfoCardItem>
          )}
          <InfoCardItem>
            <Text $type="smallBold" $color="gray">
              Owner / Director
            </Text>
            <Text $type="caption">{project.owner}</Text>
          </InfoCardItem>
          <InfoCardItem>
            <Text $type="smallBold" $color="gray">
              Status
            </Text>
            <Text $type="caption">{renderProjectStatus()}</Text>
          </InfoCardItem>
        </InfoCardGroup>
        <InfoCardGroupFlex>
          <InfoCardGroupFlexInner>
            <InfoCardItem>
              <Text $type="smallBold" $color="gray">
                Network
              </Text>
              <Text $type="caption">
                {project.network === 0 ? "Facebook" : "Youtube"}
              </Text>
            </InfoCardItem>
            <InfoCardItem>
              <Text $type="smallBold" $color="gray">
                Created / Modified
              </Text>
              <Text $type="caption">
                {DateTime.fromISO(project.updatedAt).toFormat("yyyy-MM-dd")}
              </Text>
            </InfoCardItem>
            <InfoCardItem>
              <Text $type="smallBold" $color="gray">
                Spendings
              </Text>
              <Text $type="caption">{renderProjectSpendings()}</Text>
            </InfoCardItem>
          </InfoCardGroupFlexInner>
        </InfoCardGroupFlex>
      </InfoCardGroupsWrapper>
      <ExportButtonHolder>
        <StyledExportButton exportOptions={exportOptions} />
      </ExportButtonHolder>
    </InfoCardBox>
  );
});

export default OptimisationTestInfoCard;
